import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { toast } from "sonner";
import { Zap, Mail, Lock, Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Retry logic for network issues
    const maxRetries = 2;
    let lastError = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        await login(email, password);
        toast.success(t('success.login'));
        navigate(from, { replace: true });
        return; // Success - exit function
      } catch (error) {
        lastError = error;
        
        // If it's a network error and we have retries left, wait and retry
        if (error.message === 'Failed to fetch' && attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms
          continue; // Retry
        }
        
        // For other errors or last retry, break the loop
        break;
      }
    }
    
    // Show error only after all retries failed
    if (lastError) {
      const errorMessage = lastError.message === 'Failed to fetch' 
        ? t('errors.networkError', 'Verbindungsfehler. Bitte versuchen Sie es erneut.')
        : lastError.message;
      toast.error(errorMessage);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" className="flex items-center gap-2 mb-8">
              <Zap className="h-8 w-8 text-volt" />
              <span className="font-display font-bold text-xl tracking-tight">VELOMETRICS</span>
              <span className="text-xs text-muted-foreground">by Loops Cycling Academy</span>
            </Link>
            <h1 className="font-display text-3xl font-bold tracking-tight uppercase mb-2">
              {t('auth.login.title')}
            </h1>
            <p className="text-muted-foreground">
              {t('auth.login.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="data-label">{t('auth.login.email')}</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="pl-10 bg-zinc-950 border-zinc-800 focus:border-volt h-12"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="data-label">{t('auth.login.password')}</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 bg-zinc-950 border-zinc-800 focus:border-volt h-12"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider h-12"
              data-testid="login-submit-btn"
            >
              <span className="flex items-center gap-2">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? t('auth.login.loading') : t('auth.login.submit')}
              </span>
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-800"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-background text-muted-foreground">{t('common.or')}</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={loginWithGoogle}
              className="w-full mt-6 border-zinc-800 hover:bg-zinc-900 h-12"
              data-testid="google-login-btn"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t('auth.login.google')}
            </Button>
          </div>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            {t('auth.login.noAccount')}{" "}
            <Link to="/register" className="text-volt hover:underline font-medium">
              {t('auth.login.register')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1569866005261-77dcd1ea68e8?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent"></div>
        </div>
      </div>
    </div>
  );
}
